import React, { useState } from "react";

import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";

import { BgpRoutingTable } from "./BgpData";
import { ReactComponent as IconDelete } from "@assets/Icons/Icon_Delete.svg";

const ROUTING_TABLE_ID_PATTERN = /^rtb-[a-zA-Z0-9]+$/i;

interface RoutingTablesFieldProps {
  routingTables: BgpRoutingTable[];
  onChange: (routingTables: BgpRoutingTable[]) => void;
}

/**
 * Edita las routing tables inbound del neighbor. No guarda nada por sí
 * mismo: el padre las manda con el Save del modal.
 */
function RoutingTablesField({ routingTables, onChange }: RoutingTablesFieldProps) {
  const [draftId, setDraftId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleAdd = () => {
    const routingTableId = draftId.trim();
    if (!ROUTING_TABLE_ID_PATTERN.test(routingTableId)) {
      setErrorMessage("Enter a valid routing table id (e.g. rtb-0abc123).");
      return;
    }
    if (routingTables.some((table) => table.routing_table === routingTableId)) {
      setErrorMessage("This routing table was already added.");
      return;
    }
    onChange([...routingTables, { routing_table: routingTableId, persist: true }]);
    setDraftId("");
    setErrorMessage("");
  };

  const handleDraftChange = (value: string) => {
    setDraftId(value);
    setErrorMessage("");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    handleAdd();
  };

  const handlePersistChange = (routingTableId: string, persist: boolean) => {
    onChange(
      routingTables.map((table) =>
        table.routing_table === routingTableId ? { ...table, persist } : table
      )
    );
  };

  const handleRemove = (routingTableId: string) => {
    onChange(routingTables.filter((table) => table.routing_table !== routingTableId));
  };

  return (
    <div className="mb-4">
      <span className="block mb-2 text-sm font-medium text-semantic-text-primary">
        IPv4 Unicast inbound routing tables
      </span>

      <div className="flex items-start gap-2">
        <GenericInput
          placeholder="rtb-…"
          fullWidth
          className="flex-1"
          value={draftId}
          onChange={handleDraftChange}
          onKeyDown={handleKeyDown}
          hasError={Boolean(errorMessage)}
          helperText={errorMessage}
        />
        <GenericButton label="Add" variant="secondary" size="md" onClick={handleAdd} />
      </div>

      {routingTables.length === 0 ? (
        <p className="mt-2 text-sm text-semantic-text-secondary">No routing tables.</p>
      ) : (
        <ul className="mt-2 flex flex-col gap-2">
          {routingTables.map((table) => (
            <li
              key={table.routing_table}
              className="flex items-center justify-between gap-3 text-sm text-semantic-text-primary"
            >
              <span className="break-all">{table.routing_table}</span>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-2 text-semantic-text-secondary">
                  Persist
                  <GenericSwitch
                    checked={table.persist}
                    onChange={(persist) => handlePersistChange(table.routing_table, persist)}
                  />
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${table.routing_table}`}
                  onClick={() => handleRemove(table.routing_table)}
                  className="
                    border border-semantic-border-secondary
                    rounded
                    bg-semantic-bg-primary
                    text-semantic-fg-secondary
                    hover:bg-semantic-state-active
                    w-8 h-8 flex items-center justify-center
                  "
                >
                  <IconDelete className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default RoutingTablesField;
